<!-- @format -->
<template>
    <div class="client-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ client.id ? 'Edit' : 'Create' }} a Partner</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <loading-svg v-if="loading" />
                <div v-else>
                    <div class="form-row profile-image">
                        <image-upload
                            id="image-upload"
                            orientation="vertical"
                            :uploadUrl="uploadUrl"
                            :placeholderImage="placeholderImage"
                            :providedImageUrl="client.logoImageUrl"
                            @fileUploadSuccess="updateImageUrl"
                        />
                    </div>
                    <div v-if="isOrganizationUser" class="form-row">
                        <div class="bordered-group">
                            <label>Account Status</label>
                            <p>
                                This determines if the account is active. This affects billing,
                                logging in, and functionality of the entire account.
                            </p>
                            <app-switch v-model="client.isActive" :size="'large'" />
                        </div>
                        <div class="bordered-group">
                            <label>Account Billing</label>
                            <p>
                                This determines if the partner will be billed for their
                                subscription.
                            </p>
                            <app-switch v-model="client.managesSubscription" :size="'large'" />
                            <app-date-picker
                                v-model="client.freeTrialEndDate"
                                label="Free Trial End Date"
                                description="This determines when they will start receiving invoices under their subscription"
                            />
                        </div>
                        <div class="bordered-group">
                            <label>Subscription Plan</label>
                            <promo-search v-model="client.promo" :initialId="client.promo.id" />
                            <div class="error-section" v-if="!isPromoValid && error">
                                <p class="red-accent">Please choose a subscription plan.</p>
                            </div>
                            <div v-if="client.promo" class="promo-details">
                                <ul>
                                    <li>
                                        <strong>Price: </strong>
                                        {{ client.promo.basePrice | currency }}
                                    </li>
                                    <li>
                                        <strong>Billing Cycle: </strong>
                                        {{
                                            client.promo.billingCycle
                                                ? `Billed every ` +
                                                  client.promo.billingCycle +
                                                  ` month(s)`
                                                : 'Not Billed'
                                        }}
                                    </li>
                                    <li>
                                        <strong>Max Contacts: </strong>
                                        {{ client.promo.contactLimit | commaDelimited }}
                                    </li>
                                    <li>
                                        <strong>Max Users: </strong>
                                        {{ client.promo.maxUsers | commaDelimited }}
                                    </li>
                                    <li>
                                        <strong>Free Users: </strong>
                                        {{ client.promo.freeUsers | commaDelimited }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div class="form-row">
                        <app-input v-model="client.name" :label="'Name'" />
                    </div>
                    <div v-if="isOrganizationUser" class="form-row">
                        <label>IP Pool</label>
                        <ip-pool-dropdown v-model="client.ipPoolName" />
                        <div class="error-section" v-if="!isIpPoolValid && error">
                            <p class="red-accent">Please choose an IP pool.</p>
                        </div>
                    </div>
                    <div class="form-row">
                        <app-input
                            v-model="client.monthlyLeadQuota"
                            label="Monthly Qualified Lead Quota"
                            type="number"
                        />
                    </div>
                    <div class="form-row">
                        <app-input
                            v-model="client.monthlyRevenueQuota"
                            label="Monthly Revenue Quota"
                            type="number"
                        />
                    </div>
                    <div class="form-row">
                        <app-input v-model="client.averageMsa" label="Average MSA" type="number" />
                    </div>
                    <div class="form-row">
                        <app-input
                            v-model="client.meetingPercent"
                            label="Meeting Percent"
                            type="number"
                            description="(0 - 100)"
                        />
                    </div>
                    <div class="form-row">
                        <app-input
                            v-model="client.opportunityPercent"
                            label="Opportunity Percent"
                            type="number"
                            description="(0 - 100)"
                        />
                    </div>
                    <div class="form-row">
                        <app-input
                            v-model="client.winPercent"
                            label="Win Percent"
                            type="number"
                            description="(0 - 100)"
                        />
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-button" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button @click="save()">Save</app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const AppSwitch = () => import('@/components/common/app-switch');
const IpPoolDropdown = () => import('@/components/common/ip-pool-dropdown');
const PromoSearch = () => import('@/components/common/promo-search');
const AppInput = () => import('@/components/common/app-input');
const ImageUpload = () => import('@/components/common/image-upload');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingSvg = () => import('@/components/svg/loading-screen-svg');
const AppDatePicker = () => import('@/components/common/app-date-picker');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'client-widget',

    components: {
        'app-button': AppButton,
        'app-switch': AppSwitch,
        'ip-pool-dropdown': IpPoolDropdown,
        'promo-search': PromoSearch,
        'app-input': AppInput,
        'image-upload': ImageUpload,
        'widget-close-svg': WidgetCloseSvg,
        'loading-svg': LoadingSvg,
        'app-date-picker': AppDatePicker,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            loading: true,
            error: false,
            client: {
                id: this.widget.clientId,
                name: null,
                isActive: true,
                managesSubscription: true,
                freeTrialEndDate: null,
                logoImageUrl: null,
                monthlyLeadQuota: 20,
                monthlyRevenueQuota: 30000,
                meetingPercent: 20,
                opportunityPercent: 50,
                winPercent: 50,
                averageMsa: 2500,
                ipPoolName: null,
                promo: {
                    id: 120,
                    name: null,
                    basePrice: 0,
                    billingCycle: 0,
                    contactLimit: 0,
                },
            },

            placeholderImage: 'https://cdn.honeycrm.com/files/company-avatar.png',
            uploadUrl: '/api/Files',
        };
    },

    computed: {
        isOrganizationUser() {
            return this.$store.getters.isOrganizationUser;
        },

        isValid() {
            return this.isPromoValid && this.isIpPoolValid;
        },

        isPromoValid() {
            return this.client.promo.id;
        },

        isIpPoolValid() {
            return this.client.ipPoolName;
        },
    },

    created() {
        if (this.client.id) {
            this.getClient();
        } else {
            this.loading = false;
        }
    },

    methods: {
        getQueryParams() {
            return [
                this.getFilterParam(),
                this.getExpandParam(),
                this.getSelectParam(),
                `$count=true`,
            ].join('&');
        },

        getFilterParam() {
            return `$filter=Id eq ${this.client.id}`;
        },

        getSelectParam() {
            return `$select=Id,Name,IsActive,ManagesSubscription,FreeTrialEndDate,LogoImageUrl,IpPoolName,MonthlyLeadQuota,MonthlyRevenueQuota,AppointmentPercent,ProposalPercent,ClosePercent,AverageMsa`;
        },

        getExpandParam() {
            return `$expand=Promo($select=Id,Name,BillingCycle,BasePrice,ContactLimit,FreeUsers,UserLimit)`;
        },

        getClient() {
            var query = `/service/Clients?${this.getQueryParams()}`;

            this.$api.get(query).then((response) => {
                var freeTrialEndDate = null;

                if (response.data.value[0].FreeTrialEndDate) {
                    freeTrialEndDate = new Date(
                        this.$moment
                            .utc(response.data.value[0].FreeTrialEndDate)
                            .format('MM/DD/YYYY')
                    );
                }

                this.client.name = response.data.value[0].Name;
                this.client.logoImageUrl = response.data.value[0].LogoImageUrl;
                this.client.ipPoolName = response.data.value[0].IpPoolName;
                this.client.isActive = !!response.data.value[0].IsActive;
                this.client.freeTrialEndDate = freeTrialEndDate;
                this.client.managesSubscription = !!response.data.value[0].ManagesSubscription;
                this.client.monthlyLeadQuota = response.data.value[0].MonthlyLeadQuota || 0;
                this.client.monthlyRevenueQuota = response.data.value[0].MonthlyRevenueQuota || 0;
                this.client.meetingPercent = response.data.value[0].AppointmentPercent || 0;
                this.client.opportunityPercent = response.data.value[0].ProposalPercent || 0;
                this.client.winPercent = response.data.value[0].ClosePercent || 0;
                this.client.averageMsa = response.data.value[0].AverageMsa || 0;
                this.client.promo =
                    response.data.value[0].Promo === null
                        ? null
                        : {
                              id: response.data.value[0].Promo.Id,
                              name: response.data.value[0].Promo.Name,
                              billingCycle: response.data.value[0].Promo.BillingCycle || 0,
                              basePrice: response.data.value[0].Promo.BasePrice || 0,
                              contactLimit:
                                  response.data.value[0].Promo.ContactLimit || 'Unlimited',
                              maxUsers: response.data.value[0].Promo.UserLimit || 'Unlimited',
                              freeUsers: response.data.value[0].Promo.FreeUsers || 0,
                          };

                this.loading = false;
            });
        },

        updateImageUrl(imageUrl) {
            this.client.logoImageUrl = imageUrl;
        },

        save() {
            this.error = !this.isValid;

            if (this.isValid) {
                if (this.client.id) {
                    this.patch();
                } else {
                    this.post();
                }
            }
        },

        post() {
            this.loading = true;

            this.postClient()
                .then(this.putIpPool)
                .then(this.putPromo)
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        'Your partner has been successfully added.'
                    );
                    EventBus.$emit('client:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.loading = false;
                    this.$helpers.showFailNotification(
                        'An error occurred while creating your partner.',
                        { error },
                    );
                });
        },

        patch() {
            this.loading = true;

            this.patchClient()
                .then(this.putIpPool)
                .then(this.putPromo)
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        'Your partner has been successfully updated.'
                    );
                    EventBus.$emit('client:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.loading = false;
                    this.$helpers.showFailNotification(
                        'An error occurred while updating your partner.',
                        { error },
                    );
                });
        },

        postClient() {
            return new Promise((resolve, reject) => {
                var freeTrialEndDate = null;
                if (this.client.freeTrialEndDate) {
                    freeTrialEndDate = this.$moment(this.client.freeTrialEndDate).format(
                        'YYYY-MM-DD'
                    );
                }

                var logoImage = this.client.logoImageUrl || this.placeholderImage;

                var request = {
                    name: this.client.name,
                    logoImageUrl: logoImage,
                    monthlyLeadQuota: this.client.monthlyLeadQuota,
                    monthlyRevenueQuota: this.client.monthlyRevenueQuota,
                    averageMsa: this.client.averageMsa,
                    appointmentPercent: this.client.meetingPercent,
                    proposalPercent: this.client.opportunityPercent,
                    closePercent: this.client.winPercent,
                    managesSubscription: this.client.managesSubscription,
                    isActive: this.client.isActive,
                    managesOwnIntegrations: true,
                    includeViewInBrowserLeads: false,
                    freeTrialEndDate: freeTrialEndDate,
                };

                this.$api
                    .post(`/api/Clients`, request)
                    .then((response) => {
                        this.client.id = response.data.Id;
                        resolve();
                    })
                    .catch(reject);
            });
        },

        patchClient() {
            var freeTrialEndDate = null;
            if (this.client.freeTrialEndDate) {
                freeTrialEndDate = this.$moment(this.client.freeTrialEndDate).format('YYYY-MM-DD');
            }

            var changes = [
                {
                    name: 'Name',
                    value: this.client.name,
                },
                {
                    name: 'LogoImageUrl',
                    value: this.client.logoImageUrl,
                },
                {
                    name: 'MonthlyLeadQuota',
                    value: this.client.monthlyLeadQuota,
                },
                {
                    name: 'MonthlyRevenueQuota',
                    value: this.client.monthlyRevenueQuota,
                },
                {
                    name: 'AppointmentPercent',
                    value: this.client.meetingPercent,
                },
                {
                    name: 'ProposalPercent',
                    value: this.client.opportunityPercent,
                },
                {
                    name: 'ClosePercent',
                    value: this.client.winPercent,
                },
                {
                    name: 'AverageMsa',
                    value: this.client.averageMsa,
                },
                {
                    name: 'ManagesSubscription',
                    value: this.client.managesSubscription,
                },
                {
                    name: 'IsActive',
                    value: this.client.isActive,
                },
                {
                    name: 'FreeTrialEndDate',
                    value: freeTrialEndDate,
                },
            ];

            return this.$api.patch(`/api/Clients/${this.client.id}`, changes);
        },

        putIpPool() {
            if (this.isOrganizationUser) {
                return this.$api.put(`/api/Clients/${this.client.id}/IpPool`, {
                    ipPoolName: this.client.ipPoolName,
                });
            }

            return;
        },

        putPromo() {
            if (this.isOrganizationUser) {
                return this.$api.put(`/api/Clients/${this.client.id}/Promo/Force`, {
                    Id: this.client.promo.id,
                });
            }

            return;
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
.client-widget .image-upload.vertical .image-upload-container {
    margin: 0 auto 15px auto;
}

.client-widget .form-row {
    margin-bottom: 20px;
}

.client-widget .bordered-group {
    border-radius: 20px;
    padding: 30px;
    border: 2px solid #e9e9ef;
    position: relative;
    max-width: 700px;
    margin: 0 auto 20px auto;
}

.client-widget .bordered-group > label {
    position: absolute;
    top: -12px;
    left: 50%;
    margin-left: -101px;
    font-size: 18px;
    font-weight: 600;
    background: #ffffff;
    display: block;
    padding: 0 30px;
}

.client-widget .bordered-group .app-switch .app-switch-container {
    margin: 0 auto;
}

.client-widget .promo-details {
    margin-top: 1em;
}

.client-widget .promo-details ul {
    margin: 0;
}
</style>
